import PropTypes from 'prop-types';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  FieldContainer,
  Label,
  Required,
  InputWrapper,
  SearchInput,
  CountBadge,
  DropdownIcon,
  DropdownList,
  DropdownItem,
  Checkbox,
  NoResults,
  LoadingText,
  SelectedSection,
  SelectedHeader,
  SelectedList,
  SelectedItem,
  SelectedItemLabel,
  RemoveButton,
  HelperText,
  SelectAllRow,
} from './StyledComponents';

const CheckIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    stroke="white"
    strokeWidth="3"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
    <path d="M6 9L1 4h10z" />
  </svg>
);

const XIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

/**
 * MultiSelect - A multi-select dropdown with split view design
 *
 * Selected items are displayed in a separate section below the input.
 * Supports search filtering, keyboard navigation, and async loading.
 *
 * @param {string} label - Label text for the select
 * @param {boolean} required - Whether the field is required
 * @param {Array} value - Array of selected values
 * @param {Function} onChange - Callback when selection changes (receives array of selected option objects)
 * @param {string} placeholder - Placeholder text for search input
 * @param {Array} options - Array of {label, value} objects
 * @param {boolean} loading - Whether options are loading
 * @param {Function} onSearch - Callback when search text changes (for async search)
 * @param {number} minSearchLength - Minimum characters before searching
 * @param {string} noResultsText - Text to show when no results
 * @param {string} helperText - Helper/error text
 * @param {boolean} error - Whether the field has an error
 * @param {string} width - Width of the field
 * @param {string} maxWidth - Max width of the field
 * @param {boolean} disabled - Whether the field is disabled
 * @param {string} selectedLabel - Label for the selected section (default: "Selected")
 * @param {boolean} showSelectAll - Show "Select All" option
 * @param {number} maxSelected - Maximum number of selections allowed
 */
const MultiSelect = ({
  label,
  required = false,
  value = [],
  onChange,
  placeholder = 'Search to add...',
  options = [],
  loading = false,
  onSearch,
  minSearchLength = 0,
  noResultsText = 'No results found',
  helperText,
  error = false,
  width = '100%',
  maxWidth,
  disabled = false,
  selectedLabel = 'Selected',
  showSelectAll = false,
  maxSelected,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  // Normalize value to array of values (not full objects)
  const selectedValues = Array.isArray(value)
    ? value.map((v) => (typeof v === 'object' ? v.value : v))
    : [];

  // Get selected option objects
  const selectedOptions = options.filter((opt) =>
    selectedValues.includes(opt.value)
  );

  // Filter options based on search text
  const filteredOptions =
    searchText.length >= minSearchLength
      ? options.filter((opt) =>
          opt.label.toLowerCase().includes(searchText.toLowerCase())
        )
      : options;

  // Check if max selections reached
  const isMaxReached = maxSelected && selectedValues.length >= maxSelected;

  // Check if all filtered options are selected
  const allFilteredSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every((opt) => selectedValues.includes(opt.value));

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsOpen(false);
        setSearchText('');
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchText(newValue);
    setIsOpen(true);
    setHighlightedIndex(-1);

    if (onSearch && newValue.length >= minSearchLength) {
      onSearch(newValue);
    }
  };

  const handleInputFocus = () => {
    if (!disabled) {
      setIsOpen(true);
    }
  };

  const toggleOption = useCallback(
    (option) => {
      const isSelected = selectedValues.includes(option.value);

      let newSelection;
      if (isSelected) {
        // Remove from selection
        newSelection = selectedOptions.filter(
          (opt) => opt.value !== option.value
        );
      } else {
        // Add to selection (if not at max)
        if (isMaxReached) return;
        newSelection = [...selectedOptions, option];
      }

      onChange(newSelection);
      // Close dropdown after selection
      setIsOpen(false);
      setSearchText('');
    },
    [selectedValues, selectedOptions, onChange, isMaxReached]
  );

  const handleRemoveItem = useCallback(
    (optionValue) => {
      const newSelection = selectedOptions.filter(
        (opt) => opt.value !== optionValue
      );
      onChange(newSelection);
    },
    [selectedOptions, onChange]
  );

  const handleSelectAll = useCallback(() => {
    if (allFilteredSelected) {
      // Deselect all filtered options
      const filteredValues = filteredOptions.map((opt) => opt.value);
      const newSelection = selectedOptions.filter(
        (opt) => !filteredValues.includes(opt.value)
      );
      onChange(newSelection);
    } else {
      // Select all filtered options (respecting maxSelected)
      const currentSelected = new Set(selectedValues);
      const newSelection = [...selectedOptions];

      for (const opt of filteredOptions) {
        if (!currentSelected.has(opt.value)) {
          if (maxSelected && newSelection.length >= maxSelected) break;
          newSelection.push(opt);
        }
      }

      onChange(newSelection);
    }
  }, [
    allFilteredSelected,
    filteredOptions,
    selectedOptions,
    selectedValues,
    maxSelected,
    onChange,
  ]);

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    const totalItems = showSelectAll
      ? filteredOptions.length + 1
      : filteredOptions.length;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < totalItems - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        if (showSelectAll && highlightedIndex === 0) {
          handleSelectAll();
        } else {
          const optionIndex = showSelectAll
            ? highlightedIndex - 1
            : highlightedIndex;
          if (optionIndex >= 0 && filteredOptions[optionIndex]) {
            toggleOption(filteredOptions[optionIndex]);
          }
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setSearchText('');
        break;
      default:
        break;
    }
  };

  const selectedCount = selectedValues.length;

  return (
    <FieldContainer width={width} maxWidth={maxWidth} ref={containerRef}>
      {label && (
        <Label>
          {label} {required && <Required>*</Required>}
        </Label>
      )}

      <InputWrapper>
        <SearchInput
          ref={inputRef}
          type="text"
          value={searchText}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          error={error}
          isOpen={isOpen}
          hasCount={selectedCount > 0}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
        />

        {selectedCount > 0 && <CountBadge>{selectedCount}</CountBadge>}

        <DropdownIcon isOpen={isOpen}>
          <ChevronIcon />
        </DropdownIcon>

        {isOpen && (
          <DropdownList role="listbox">
            {loading ? (
              <LoadingText>Loading...</LoadingText>
            ) : options.length === 0 ? (
              <NoResults>No options available</NoResults>
            ) : filteredOptions.length === 0 ? (
              <NoResults>
                {searchText.length < minSearchLength
                  ? `Type at least ${minSearchLength} characters to search`
                  : noResultsText}
              </NoResults>
            ) : (
              <>
                {showSelectAll && filteredOptions.length > 1 && (
                  <SelectAllRow
                    onClick={handleSelectAll}
                    role="option"
                    aria-selected={allFilteredSelected}
                  >
                    <Checkbox checked={allFilteredSelected}>
                      {allFilteredSelected && <CheckIcon />}
                    </Checkbox>
                    {allFilteredSelected ? 'Deselect All' : 'Select All'}
                  </SelectAllRow>
                )}
                {filteredOptions.map((option, index) => {
                  const isSelected = selectedValues.includes(option.value);
                  const highlightIndex = showSelectAll ? index + 1 : index;
                  const isDisabledOption = !isSelected && isMaxReached;

                  return (
                    <DropdownItem
                      key={option.value}
                      isHighlighted={highlightedIndex === highlightIndex}
                      onClick={() => !isDisabledOption && toggleOption(option)}
                      role="option"
                      aria-selected={isSelected}
                      style={{
                        opacity: isDisabledOption ? 0.5 : 1,
                        cursor: isDisabledOption ? 'not-allowed' : 'pointer',
                      }}
                    >
                      <Checkbox checked={isSelected}>
                        {isSelected && <CheckIcon />}
                      </Checkbox>
                      {option.label}
                    </DropdownItem>
                  );
                })}
              </>
            )}
          </DropdownList>
        )}
      </InputWrapper>

      {/* Selected Items Section - only show when items are selected */}
      {selectedOptions.length > 0 && (
        <SelectedSection>
          <SelectedHeader>
            {selectedLabel} ({selectedCount})
            {maxSelected && ` - Max ${maxSelected}`}
          </SelectedHeader>
          <SelectedList>
            {selectedOptions.map((option) => (
              <SelectedItem key={option.value}>
                <SelectedItemLabel>{option.label}</SelectedItemLabel>
                <RemoveButton
                  type="button"
                  onClick={() => handleRemoveItem(option.value)}
                  disabled={disabled}
                  aria-label={`Remove ${option.label}`}
                >
                  <XIcon />
                </RemoveButton>
              </SelectedItem>
            ))}
          </SelectedList>
        </SelectedSection>
      )}

      {helperText && <HelperText error={error}>{helperText}</HelperText>}
    </FieldContainer>
  );
};

MultiSelect.propTypes = {
  label: PropTypes.string,
  required: PropTypes.bool,
  value: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.shape({
        label: PropTypes.string,
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      }),
    ])
  ),
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
        .isRequired,
    })
  ),
  loading: PropTypes.bool,
  onSearch: PropTypes.func,
  minSearchLength: PropTypes.number,
  noResultsText: PropTypes.string,
  helperText: PropTypes.string,
  error: PropTypes.bool,
  width: PropTypes.string,
  maxWidth: PropTypes.string,
  disabled: PropTypes.bool,
  selectedLabel: PropTypes.string,
  showSelectAll: PropTypes.bool,
  maxSelected: PropTypes.number,
};

export default MultiSelect;
